import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, ShieldCheck } from "lucide-react";
import { services as footerServices } from "@/data/servicesData";

const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Footer() {
  return (
    <footer className="bg-navy-950 text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-5">
            <div className="inline-block bg-white rounded-2xl px-4 py-3 shadow-lg" data-testid="footer-logo-badge">
              <img src="/logo.png" alt="ClariDent RCM — We clear the way. You care for smiles." className="h-24 w-auto" />
            </div>
          </div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-teal-400 mb-3">We Clear The Way. You Care For Smiles.</p>
          <p className="text-sm leading-relaxed text-slate-400">
            Revenue cycle management built on experience, transparency, and results.
          </p>
          <div className="flex items-center gap-2 mt-5 text-teal-400 text-xs font-bold tracking-widest uppercase">
            <ShieldCheck size={16} /> HIPAA-Ready Security Practices
          </div>
        </div>
        <div>
          <h4 className="font-display font-bold text-white mb-5 text-sm tracking-widest uppercase">Company</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/" className="hover:text-teal-400 transition-colors" data-testid="footer-link-home">Home</Link></li>
            <li><Link to="/about" className="hover:text-teal-400 transition-colors" data-testid="footer-link-about">About Us</Link></li>
            <li><Link to="/pricing" className="hover:text-teal-400 transition-colors" data-testid="footer-link-pricing">Pricing</Link></li>
            <li><Link to="/resources" className="hover:text-teal-400 transition-colors" data-testid="footer-link-resources">Resources & Blog</Link></li>
            <li><Link to="/faq" className="hover:text-teal-400 transition-colors" data-testid="footer-link-faq">FAQ</Link></li>
            <li><Link to="/careers" className="hover:text-teal-400 transition-colors" data-testid="footer-link-careers">Careers</Link></li>
            <li><Link to="/contact" className="hover:text-teal-400 transition-colors" data-testid="footer-link-contact">Contact Us</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-white mb-5 text-sm tracking-widest uppercase">Services</h4>
          <ul className="space-y-3 text-sm text-slate-400">
            {footerServices.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-teal-400 transition-colors" data-testid={`footer-service-${s.slug}`}>
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-white mb-5 text-sm tracking-widest uppercase">Get In Touch</h4>
          <a
            href="mailto:Billing@claridentrcm.com"
            className="flex items-center gap-2 text-sm hover:text-teal-400 transition-colors mb-6"
            data-testid="footer-email-link"
          >
            <Mail size={16} /> Billing@claridentrcm.com
          </a>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/company/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-teal-600 hover:border-teal-600 transition-colors">
              <Linkedin size={18} />
            </a>
            <a href="https://www.instagram.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-teal-600 hover:border-teal-600 transition-colors">
              <Instagram size={18} />
            </a>
            <a href="https://www.facebook.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-teal-600 hover:border-teal-600 transition-colors">
              <Facebook size={18} />
            </a>
            <a href="https://x.com/claridentrcm" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" data-testid="footer-x" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-teal-600 hover:border-teal-600 transition-colors">
              <XIcon size={16} />
            </a>
          </div>
          <Link
            to="/book"
            data-testid="footer-audit-btn"
            className="inline-block mt-8 rounded-full bg-teal-600 px-6 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-0.5"
          >
            Book Your Free Audit
          </Link>
        </div>
      </div>
      <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
        Copyright © 2026 ClariDent RCM. All rights reserved. · Focus on your patients. We'll focus on your revenue.
        <span className="mx-2">·</span>
        <Link to="/privacy" className="hover:text-teal-400 transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
        <span className="mx-2">·</span>
        <Link to="/terms" className="hover:text-teal-400 transition-colors" data-testid="footer-link-terms">Terms & Conditions</Link>
      </div>
    </footer>
  );
}
